import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { sendFriendlyRequest } from '../../../redux/actions/Friendly';
import { hidePopupWindow } from '../../../redux/actions/Popups';
import {
  getFriendlyLoading,
  friendlyTeams,
  getFriendlyOfferLoading,
  getFriendlyOfferResponse,
} from '../../../redux/selector/Friendly';

import {
  StyledPopUp,
  StyledMultisectionPopup,
  StyledLoader,
  StyledCross,
  StyledSubmitButton,
} from '../styled';
import {
  StyledLeagueContainer,
  StyledTeam,
  StyledLeagueHeader,
  StyledContainer,
  StyledHeader,
  StyledTeamsContainer,
  StyledCrest,
  StyledCrestFallback,
  StyledTeamName,
} from './styled';
import InviteServerSelect from '../../InviteServerSelect';
import useInviteServerFilter from '../../../hooks/useInviteServerFilter';

const teamCrest = (team, crestByTeamId) =>
  team.symbol || (team._id != null ? crestByTeamId[String(team._id)] : '');

const FriendlyPopup = ({ popupName, leaguesList }) => {
  const [activeTeamSelectId, setActiveTeamId] = React.useState(null);
  const dispatch = useDispatch();
  const friendlyData = useSelector(friendlyTeams);
  const isFriendlyLoading = useSelector(getFriendlyLoading);
  const isFriendlyOfferLoading = useSelector(getFriendlyOfferLoading);
  const friendlyOfferResponse = useSelector(getFriendlyOfferResponse);
  const {
    universeOptions,
    selectedUniverseName,
    setSelectedUniverseName,
    leagues,
    isHomeServer,
    remoteStatus,
    getTeamsForLeague,
    crestByTeamId,
  } = useInviteServerFilter({
    inviteTeams: friendlyData,
    fallbackLeagues: leaguesList,
  });

  const sendFriendly = (teamId) => {
    setActiveTeamId(teamId);
    dispatch(sendFriendlyRequest(teamId));
  };

  const hidePopup = () => {
    dispatch(hidePopupWindow(popupName));
  };

  const buttonText = (teamData, offerResponse) => {
    if (teamData.bot) {
      return 'Bot';
    }
    if (offerResponse === teamData._id) {
      return 'Sent';
    }
    return 'Select';
  };

  const showRemoteEmpty = !isHomeServer && remoteStatus === 'empty';
  const showRemoteLoader = !isHomeServer && remoteStatus === 'loading';

  const renderLeagues = (leagueItems) =>
    leagueItems.map((league) => {
      const teamsForLeague = getTeamsForLeague(league);
      return (
        <StyledLeagueContainer key={league._id || league.name}>
          <StyledLeagueHeader>{league.name}</StyledLeagueHeader>
          <div>
            {teamsForLeague.map((team) => {
              const crest = teamCrest(team, crestByTeamId);
              return (
                <StyledTeam key={team._id}>
                  {crest ? (
                    <StyledCrest src={crest} alt="" />
                  ) : (
                    <StyledCrestFallback aria-hidden>
                      {(team.name || '?').charAt(0)}
                    </StyledCrestFallback>
                  )}
                  <StyledTeamName>{team.name}</StyledTeamName>
                  <StyledSubmitButton
                    isGrey={team.bot}
                    primary
                    disabled={
                      isFriendlyOfferLoading && activeTeamSelectId === team._id
                    }
                    isCompleted={friendlyOfferResponse === team._id}
                    onClick={() => {
                      sendFriendly(team._id);
                    }}
                  >{`${buttonText(team, friendlyOfferResponse)}`}</StyledSubmitButton>
                </StyledTeam>
              );
            })}
          </div>
        </StyledLeagueContainer>
      );
    });

  return (
    <StyledPopUp>
      <StyledMultisectionPopup>
        {isFriendlyLoading ? (
          <StyledLoader type="Bars" color="var(--tol-header-accent)" height={100} width={100} />
        ) : (
          <StyledContainer>
            <StyledHeader>
              Friendly match - Select an opponent
              <StyledCross onClick={() => hidePopup()} />
            </StyledHeader>
            <InviteServerSelect
              embedded
              value={selectedUniverseName}
              options={universeOptions}
              onChange={setSelectedUniverseName}
            />
            {showRemoteLoader ? (
              <StyledLoader type="Bars" color="var(--tol-header-accent)" height={80} width={80} />
            ) : showRemoteEmpty ? (
              <StyledTeamsContainer>
                {leagues.slice(0, 3).map((league) => (
                  <StyledLeagueContainer key={league._id || league.name}>
                    <StyledLeagueHeader>{league.name}</StyledLeagueHeader>
                    <StyledTeam>
                      <StyledCrestFallback aria-hidden>—</StyledCrestFallback>
                      <StyledTeamName>No teams available</StyledTeamName>
                    </StyledTeam>
                  </StyledLeagueContainer>
                ))}
              </StyledTeamsContainer>
            ) : (
              <StyledTeamsContainer>{renderLeagues(leagues)}</StyledTeamsContainer>
            )}
          </StyledContainer>
        )}
      </StyledMultisectionPopup>
    </StyledPopUp>
  );
};

export default FriendlyPopup;
